/**
 * PipelineDefaultsBlock — "These are the default models for this pipeline."
 *
 * The row already had a Default dropdown and a Use Default button, but a reader
 * could not see at a glance what the default IS or how it differs from what is
 * actually running: the dropdown shows the default's model_id with no label
 * saying that is what it means, and `is_on_default` only reached the UI as a
 * chip on the collapsed line.
 *
 * Three honest states, and no fourth:
 *   MATCH  — the active model IS the default. Say so plainly; no action offered.
 *   DIFFER — show the two side by side, default first, with Use Default beside
 *            them so the fix is where the discrepancy is read.
 *   NONE   — no default recorded. Say that. Never render a blank where a model
 *            id belongs — an empty box reads as "loading", and a screen may
 *            only claim what the code has.
 */

import React from 'react';
import { Box, Typography, Button, Chip } from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { colors, typography } from '../../../theme/designTokens';
import { PipelineModel, PipelineModelRef } from '../../../services/quillAdminService';

const HEADING = 'These are the default models for this pipeline.';

/** One labelled {provider, model_id} cell. */
const ModelCell: React.FC<{ label: string; value: PipelineModelRef; emphasis?: boolean; testId: string }> = ({
  label,
  value,
  emphasis = false,
  testId,
}) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        color: colors.gray[600],
      }}
    >
      {label}
    </Typography>
    <Typography
      data-testid={testId}
      sx={{
        fontFamily: typography.fonts.mono,
        fontSize: typography.sizes.sm,
        fontWeight: emphasis ? 700 : 500,
        color: emphasis ? colors.warning.dark : colors.gray[900],
        wordBreak: 'break-all',
      }}
    >
      {value.provider ? `${value.provider} · ${value.model_id}` : value.model_id}
    </Typography>
  </Box>
);

export interface PipelineDefaultsBlockProps {
  pipeline: PipelineModel;
  /** Fixed rows show their default but can never be re-pointed. */
  isFixed: boolean;
  onUseDefault: (pipeline: PipelineModel) => void;
}

export const PipelineDefaultsBlock: React.FC<PipelineDefaultsBlockProps> = ({
  pipeline,
  isFixed,
  onUseDefault,
}) => {
  const hasDefault = Boolean(pipeline.default?.model_id);
  const matches = hasDefault && pipeline.is_on_default;

  return (
    <Box
      data-testid={`pipeline-defaults-${pipeline.pipeline_key}`}
      sx={{
        mt: 2,
        p: 1.5,
        borderRadius: '12px',
        backgroundColor: colors.gray[50],
        border: `1px solid ${colors.gray[200]}`,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
        {HEADING}
      </Typography>

      {!hasDefault ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          No default model is recorded for this pipeline, so there is nothing to return it to.
          Set one with the Default dropdown above.
        </Typography>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 3,
              flexWrap: 'wrap',
              mb: 1,
            }}
          >
            <ModelCell
              label="Default"
              value={pipeline.default}
              testId={`defaults-default-${pipeline.pipeline_key}`}
            />
            {!matches && (
              <ModelCell
                label="Running now"
                value={pipeline.active}
                emphasis
                testId={`defaults-active-${pipeline.pipeline_key}`}
              />
            )}
          </Box>

          {matches ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 18, color: colors.info.dark }} />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                The active model matches this default.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                The active model is different from this default right now.
              </Typography>
              {isFixed ? (
                <Chip label="fixed — not changeable here" size="small" />
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<UndoIcon />}
                  onClick={() => onUseDefault(pipeline)}
                  sx={{ textTransform: 'none' }}
                >
                  Use Default
                </Button>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default PipelineDefaultsBlock;
